// src/ChatLearningSpace.tsx
// -------------------------------------------------------
// Refactored ChatLearningSpace — wired to GameProfile system.
// Replaces hardcoded KCV logic with profile-driven dispatch.
// -------------------------------------------------------

import React from "react";
import TalkingAvatar from "./TalkingAvatar";
import "./css/TeachingAvatar.css";
import "./css/ChatLearningSpace.css";
import DigiBoardPanel, { Msg as DigiMsg } from "./chat/DigiBoardPanel";

import { DEFAULT_BELLA_SYSTEM_PROMPT } from "./chat/defaultBellaPrompt";
import { ensureSeeded, getActiveVersionOrDefault } from "./chat/promptStore";

// NEW: GameProfile imports
import type { GameProfile, GameState, GameEvent } from "./config/gameProfiles/GameProfile";
import { createInitialGameState, advanceGameState } from "./config/gameProfiles/GameProfile";
import { detectProfile } from "./config/gameProfiles/profileRegistry";
import {
  gatekeepBoard,
  shouldFetchExternalBoard,
  safeParseBoard,
  buildControlPrompt as buildControlPromptFromProfile,
  buildBoardInstruction,
} from "./chat/boardGatekeeper";

import { useRive, Layout, Fit, Alignment } from "@rive-app/react-canvas";

import {
  loadActiveLessonBlueprint,
  startCTPlayback,
  nextCTCue,
} from "./chat/lessonStore";

/* ===================================================== */
/* ===================== TYPES ========================== */
/* ===================================================== */

type Role = "system" | "user" | "assistant";
type Msg = { role: "system" | "user" | "assistant"; content: string };

function unwrapReplyPayload(data: any): { reply: string; script: any | null; board: any | null } {
  if (!data) return { reply: "", script: null, board: null };

  // Normal case: proxy returns structured fields
  if (typeof data.reply === "string" && (data.script || data.board)) {
    return {
      reply: String(data.reply || "").trim(),
      script: data.script ?? null,
      board: data.board ?? null,
    };
  }

  // Sometimes reply itself is a JSON string (double-wrapped)
  if (typeof data.reply === "string") {
    const s = data.reply.trim();
    if (s.startsWith("{") && s.includes('"reply"') && s.includes('"script"')) {
      try {
        const inner = JSON.parse(s);
        return {
          reply: String(inner.reply || "").trim(),
          script: inner.script ?? null,
          board: inner.board ?? inner?.script?.beats?.[0]?.board ?? null,
        };
      } catch {
        // fall through
      }
    }
    return { reply: s, script: null, board: null };
  }

  // If the entire response is a JSON string
  if (typeof data === "string") {
    const s = data.trim();
    if (s.startsWith("{")) {
      try {
        const inner = JSON.parse(s);
        return {
          reply: String(inner.reply || "").trim(),
          script: inner.script ?? null,
          board: inner.board ?? inner?.script?.beats?.[0]?.board ?? null,
        };
      } catch {}
    }
    return { reply: s, script: null, board: null };
  }

  // Fallback: try read OpenAI-style content
  const content = data?.choices?.[0]?.message?.content;
  if (typeof content === "string") return { reply: content.trim(), script: null, board: null };

  return { reply: "", script: null, board: null };
}

type Props = {
  aiProvider?: "openai" | "ollama";
  voiceProvider?: "eleven" | "browser";
  openaiUrl?: string;
  ollamaUrl?: string;
  digiboardUrl?: string;
};

const DEFAULT_MODEL = "llama3.1";
const DEFAULT_OLLAMA_CHAT = "http://127.0.0.1:11434/api/chat";
const DEFAULT_OPENAI_CHAT =
  (process.env.REACT_APP_OPENAI_CHAT_URL as string) || "/api/openai-chat.php";
const DEFAULT_DIGIBOARD =
  (process.env.REACT_APP_DIGIBOARD_URL as string) || "/api/digiboard.php";

const BELLA_RIV_SRC = "avatar/bella-avatar.riv";

/* ===================================================== */
/* ===================== UTIL =========================== */
/* ===================================================== */

function safeTrim(s: string, max = 160) {
  const t = (s || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length > max ? t.slice(0, max).trim() + "\u2026" : t;
}

function safeArray(items: any, max = 8): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((x: any) => safeTrim(String(x ?? ""), 90))
    .filter(Boolean)
    .slice(0, max);
}

/* ===================================================== */
/* =================== PROMPT PARSE ===================== */
/* ===================================================== */

function parseIntroTopicsAndLessonTitle(systemPrompt: string) {
  const text = systemPrompt || "";
  const titleMatch = text.match(/LESSON TITLE:\s*([^\n]+)/i);
  const lessonTitle = (titleMatch?.[1] || "").trim();

  const introBlockMatch = text.match(
    /INTRO TOPICS\s*\(DIGIBOARD\):([\s\S]*?)\n\s*(?:LESSON TITLE:|={3,}|BELANGRIJK|WERKWIJZE|START|IMPORTANT|MODUS-SYSTEEM|EXAM-QUESTION RULES)/i
  );
  const block = introBlockMatch?.[1] || "";
  const topics = block
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.replace(/^-+\s*/, "").trim())
    .filter(Boolean);

  const startMatch = text.match(/START\s*[\s\S]*?Begin met:\s*([^\n]+)/i);
  const startQuestion = (startMatch?.[1] || "").trim();

  return { lessonTitle, topics, startQuestion };
}

/* ===================================================== */
/* ===================== DIGIBOARD ====================== */
/* ===================================================== */

type DigiBoard =
  | {
      version: number;
      scene?: { title?: string; unit?: string };
      meta?: { mode?: "overview" | "question" | "explanation" | "scaffold" };
      blocks: Array<any>;
      layout?: string;
      phase?: string;
    }
  | null;

function normalizeBoard(input: any, prev: DigiBoard): DigiBoard {
  if (!input || typeof input !== "object") return prev;
  if (Array.isArray(input.blocks)) {
    return {
      version: input.version || 2,
      scene: input.scene && typeof input.scene === "object" ? input.scene : undefined,
      meta: input.meta && typeof input.meta === "object" ? input.meta : undefined,
      blocks: input.blocks,
      layout: input.layout,
      phase: input.phase,
    };
  }
  return prev;
}

/* ===================================================== */
/* ===================== AVATAR ========================= */
/* ===================================================== */

function BellaRiveBig() {
  const { RiveComponent } = useRive({
    src: BELLA_RIV_SRC,
    stateMachines: "State Machine 1",
    autoplay: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.BottomCenter }),
  });

  return (
    <div className="chatls-riveWrap">
      <RiveComponent className="chatls-rive" />
    </div>
  );
}

function chunkTextWithStarts(text: string, maxLen = 90) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return { pages: [""], starts: [0] };

  const pages: string[] = [];
  const starts: number[] = [];
  let i = 0;

  while (i < clean.length) {
    starts.push(i);
    let end = Math.min(i + maxLen, clean.length);
    const slice = clean.slice(i, end);

    const punctIdx = Math.max(
      slice.lastIndexOf(". "),
      slice.lastIndexOf("! "),
      slice.lastIndexOf("? "),
      slice.lastIndexOf("; "),
      slice.lastIndexOf(": ")
    );

    if (punctIdx > Math.floor(maxLen * 0.55)) {
      end = i + punctIdx + 1;
    } else {
      const spaceIdx = slice.lastIndexOf(" ");
      if (spaceIdx > Math.floor(maxLen * 0.6)) end = i + spaceIdx;
    }

    pages.push(clean.slice(i, end).trim());
    i = Math.max(end, i + 1);
  }

  return { pages, starts };
}

/* ===================================================== */
/* ===================== MAIN =========================== */
/* ===================================================== */

export default function ChatLearningSpace({
  aiProvider = "openai",
  voiceProvider = "browser",
  openaiUrl = DEFAULT_OPENAI_CHAT,
  ollamaUrl = DEFAULT_OLLAMA_CHAT,
  digiboardUrl = DEFAULT_DIGIBOARD,
}: Props) {
    // ✅ Safety: if openaiUrl is misconfigured (or points to TTS), fall back to the chat proxy.
  const resolvedOpenaiUrl =
    openaiUrl && openaiUrl.includes("openai-chat")
      ? openaiUrl
      : DEFAULT_OPENAI_CHAT;
  // ---- Prompt version ----
  const [activeVersion] = React.useState(() => {
    ensureSeeded(DEFAULT_BELLA_SYSTEM_PROMPT);
    return getActiveVersionOrDefault(DEFAULT_BELLA_SYSTEM_PROMPT);
  });

  const systemPrompt = activeVersion.systemPrompt;

  // ---- GameProfile: auto-detect from system prompt ----
  const activeProfile: GameProfile = React.useMemo(
    () => detectProfile(systemPrompt),
    [systemPrompt]
  );

  // ---- GameState: single object replaces scattered phase/turn state ----
  const [gameState, setGameState] = React.useState<GameState>(() =>
    createInitialGameState(activeProfile)
  );

  // Ensure initial phase is committed immediately on mount
React.useEffect(() => {
  setGameState(createInitialGameState(activeProfile));
}, [activeProfile.id]);

// ✅ IMPORTANT: clear any previous board override when switching profiles
React.useEffect(() => {
  setBoardOverride(null);
  setBoard(null);
}, [activeProfile.id]);

  // Convenience: current phase
const enginePhase = gameState.phase;



  // ---- Messages ----
  const [messages, setMessages] = React.useState<Msg[]>(() => [
    { role: "system", content: systemPrompt },
    {
      role: "assistant",
      content: activeProfile.readyGateEnabled
        ? (activeVersion as any)?.welcomeMessage || activeProfile.readyPrompt
        : (activeVersion as any)?.welcomeMessage || "",
    },
  ]);


  // ✅ UI-driven intro: always show intro BEFORE the first user message
const hasUserSpoken = React.useMemo(
  () => messages.some((m) => m.role === "user"),
  [messages]
);

// ✅ What DigiBoard should consider the "phase" for intro rendering
const boardPhase =
  (!hasUserSpoken && activeProfile.readyGateEnabled) ? "INTRO" : enginePhase;

  const [activeLesson, setActiveLesson] = React.useState<any>(null);
const scriptedPlayingRef = React.useRef(false);
const waitingForSpeakEndRef = React.useRef(false);

React.useEffect(() => {
  if (activeProfile.id === "design-thinking") {
    setActiveLesson(null);
    return;
  }
  loadActiveLessonBlueprint()
    .then(setActiveLesson)
    .catch(() => setActiveLesson(null));
}, [activeProfile.id]);

  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  // ---- DigiBoard state ----
  const [board, setBoard] = React.useState<DigiBoard>(null);
  const boardRef = React.useRef<DigiBoard>(null);
  React.useEffect(() => { boardRef.current = board; }, [board]);

  const boardReqIdRef = React.useRef(0);
  const boardAbortRef = React.useRef<AbortController | null>(null);

  
  const [boardOverride, setBoardOverride] = React.useState<any>(null);

  

  const visibleMessages = React.useMemo(() => messages.filter((m) => m.role !== "system"), [messages]);

  const { lessonTitle, topics } = React.useMemo(
    () => parseIntroTopicsAndLessonTitle(systemPrompt),
    [systemPrompt]
  );

  // ---- Last assistant for TTS ----
  const lastAssistant = React.useMemo(() => {
    const last = [...messages].reverse().find((m) => m.role === "assistant");
    return last?.content || "";
  }, [messages]);

  const lastAssistantVisibleIndex = React.useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].role === "assistant") return i;
    }
    return -1;
  }, [visibleMessages]);

  const lastUserVisibleIndex = React.useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].role === "user") return i;
    }
    return -1;
  }, [visibleMessages]);

  // ---- Speaking / TTS ----
  const [pageIndex, setPageIndex] = React.useState(0);
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const [charIndex, setCharIndex] = React.useState(0);

  const { pages: currentPages, starts: currentStarts } = React.useMemo(
    () => chunkTextWithStarts(lastAssistant, 90),
    [lastAssistant]
  );

  // Auto-advance scripted lesson when speech finishes
React.useEffect(() => {
  if (!activeLesson) return;
  if (!scriptedPlayingRef.current) return;
  if (!waitingForSpeakEndRef.current) return;

  // Wait until avatar stops speaking
  if (isSpeaking) return;

  waitingForSpeakEndRef.current = false;

  (async () => {
    const nextText = await nextCTCue(activeLesson);

    if (!nextText) {
      scriptedPlayingRef.current = false;
      setMessages((prev: Msg[]) => [...prev, { role: "assistant", content: "Klaar! Wil je doorgaan?" }]);
      return;
    }

    setMessages((prev: Msg[]) => [...prev, { role: "assistant", content: nextText }]);
    waitingForSpeakEndRef.current = true;
  })();
}, [isSpeaking, activeLesson, setMessages]);

  React.useEffect(() => { setPageIndex(0); setCharIndex(0); }, [lastAssistant]);

  React.useEffect(() => {
    if (!isSpeaking) return;
    let next = 0;
    for (let p = 0; p < currentStarts.length; p++) {
      if (currentStarts[p] <= charIndex) next = p;
      else break;
    }
    setPageIndex(next);
  }, [charIndex, currentStarts, isSpeaking]);

  // ---- Transcript scrolling ----
  const transcriptRef = React.useRef<HTMLDivElement | null>(null);
  const activeChunkRef = React.useRef<HTMLDivElement | null>(null);
  const lastUserBubbleRef = React.useRef<HTMLDivElement | null>(null);
  const rightBottomRef = React.useRef<HTMLDivElement | null>(null);

  const userScrolledAwayRef = React.useRef(false);
  const forceStickToBottomRef = React.useRef(false);

  const onTranscriptScroll = () => {
    const el = transcriptRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledAwayRef.current = distanceFromBottom > 160;
    if (userScrolledAwayRef.current) forceStickToBottomRef.current = false;
  };

  React.useEffect(() => {
    if (!isSpeaking) return;
    if (userScrolledAwayRef.current) return;
    activeChunkRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
  }, [pageIndex, isSpeaking]);

  React.useEffect(() => {
    if (!forceStickToBottomRef.current) return;
    requestAnimationFrame(() => {
      rightBottomRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
    });
  }, [visibleMessages.length]);

  React.useEffect(() => {
    if (!lastAssistant) return;
    userScrolledAwayRef.current = false;
    forceStickToBottomRef.current = true;
    requestAnimationFrame(() => {
      if (activeChunkRef.current) activeChunkRef.current.scrollIntoView({ block: "center", behavior: "auto" });
      else rightBottomRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
    });
  }, [lastAssistant]);

  // ---- Board update (Pipeline A: digiboard.php, for KCV) ----

  function boardWindow(msgs: Msg[], maxVisible = 10) {
    const vis = msgs.filter((m) => m.role !== "system");
    return vis.slice(Math.max(0, vis.length - maxVisible));
  }

  async function updateBoardAfterAssistant(allMessages: Msg[]) {
    // Skip if avatar-driven (DT) or if boardOverride is set
    if (!shouldFetchExternalBoard(activeProfile)) return;
    if (boardOverride) return;

    // Clear any stale board when switching profiles or when we are in intro mode
React.useEffect(() => {
  setBoardOverride(null);
  setBoard(null);
}, [activeProfile.id]);

React.useEffect(() => {
  if (boardForcedMode === "intro") {
    setBoardOverride(null);
    setBoard(null);
  }
}, [boardForcedMode]);

    const reqId = ++boardReqIdRef.current;
    if (boardAbortRef.current) {
      try { boardAbortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    boardAbortRef.current = controller;

    const assistantLastLocal = [...allMessages].reverse().find((m) => m.role === "assistant")?.content || "";
    const assistantHash = `${assistantLastLocal.length}:${assistantLastLocal.slice(0, 120)}`;

    try {
      const payload = {
        systemPrompt,
        messages: boardWindow(allMessages, 10),
        assistantLast: assistantLastLocal,
        assistantHash,
        currentBoard: boardRef.current,
      };

      const res = await fetch(digiboardUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const data = await res.json().catch(() => null);
      if (reqId !== boardReqIdRef.current) return;
      if (!res.ok || !data?.ok) throw new Error(data?.error || "digiboard error");
      if (data?.assistantHash && data.assistantHash !== assistantHash) return;

      const next = normalizeBoard(data.board ?? data, boardRef.current);
      if (next) setBoard(next);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      if (reqId !== boardReqIdRef.current) return;
    }
  }



  // ---- OpenAI chat call ----

async function callOpenAIChat(nextMessages: Msg[], userText: string, stateForModel: GameState) {
  // These sometimes throw when profile/state is unexpected.
  // We never want that to block the OpenAI call.
  let controlPrompt = "";
  let boardInstruction = "";

  try {
controlPrompt = buildControlPromptFromProfile(activeProfile, stateForModel, userText) || "";
  } catch (e) {
    console.error("[buildControlPromptFromProfile] crashed", e);
    controlPrompt = "";
  }

  try {
    boardInstruction = buildBoardInstruction(activeProfile, stateForModel) || "";
  } catch (e) {
    console.error("[buildBoardInstruction] crashed", e);
    boardInstruction = "";
  }

  const assembled: Msg[] = [
    nextMessages[0], // main system prompt
    ...(controlPrompt.trim()
      ? [{ role: "system" as Role, content: controlPrompt }]
      : []),
    ...(boardInstruction.trim()
      ? [{ role: "system" as Role, content: boardInstruction }]
      : []),
    ...nextMessages.slice(1),
  ];

  console.log("[OpenAI] POST", resolvedOpenaiUrl, {
    want_script: true,
    profileId: activeProfile.id,
   gamePhase: stateForModel.phase,
  });

  const res = await fetch(resolvedOpenaiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages: assembled,
max_output_tokens: activeProfile.boardSource === "avatar-driven" ? 2500 : 800,
      want_script: true,
      profileId: activeProfile.id,
     gamePhase: stateForModel.phase,
    }),
  });

  const raw = await res.text();
  let data: any = null;
  try {
    data = JSON.parse(raw);
  } catch {
    console.error("[OpenAI proxy non-JSON response]", res.status, raw);
    throw new Error("openai proxy returned non-JSON");
  }

  if (!res.ok || !data?.ok) {
    console.error("[OpenAI proxy failed]", res.status, data || raw);
    throw new Error(data?.error || raw || "openai proxy error");
  }

  // ✅ IMPORTANT: use the unwrapped board/script, not data.board directly
  const unwrapped = unwrapReplyPayload(data);

  const reply = (unwrapped.reply || "").trim();
  const script = unwrapped.script ?? null;

  // Prefer explicit board, else script.beats[0].board
  const board =
    unwrapped.board ??
    (script && (script as any)?.beats?.[0]?.board ? (script as any).beats[0].board : null);

  return { reply, script, board };
}
  // ---- Send message ----

  async function send() {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setLoading(true);

    // ---- Classify user input via profile ----
    const userEvent = activeProfile.classifyUserInput(text, gameState);
    // What the user typed (we show this in chat)
const userText = text;

// What we send to the AI (we may convert "ja" -> "START" in ready gate)
let modelText = text;

// ✅ If the profile has a ready gate and the user said "ready" in the initial phase,
// send START to the model so the mission actually begins.
if (
  activeProfile.readyGateEnabled &&
  gameState.phase === activeProfile.initialPhase &&
  userEvent.type === "ready"
) {
  modelText = "START";
}

// ---- Handle ready gate (all profiles) ----
const isIntroPhase =
  gameState.phase === activeProfile.initialPhase && activeProfile.readyGateEnabled;
const isWaiting = isIntroPhase && gameState.turnInPhase === 0;

if (
  isWaiting ||
  gameState.phase === "READY_WAIT" ||
  (gameState.phase === "INTRO" && userEvent.type !== "ready")
) {
  if (userEvent.type !== "ready") {
    // Not ready yet — re-prompt
    const baseLocal: Msg[] = [
      ...messages,
      { role: "user", content: text },
      { role: "assistant", content: activeProfile.readyPrompt },
    ];
    setMessages(baseLocal);
    setGameState((prev) => ({
      ...prev,
      phase: prev.phase === "INTRO" ? "READY_WAIT" : prev.phase,
      turnInPhase: prev.turnInPhase + 1,
    }));
    setLoading(false);
    return;
  }
}

// ✅ Compute the next state once and use it for the model call
const nextState = advanceGameState(activeProfile, gameState, userEvent);
setGameState(nextState);

    // Append user message
    const baseNext: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(baseNext);


    // ------------------------
// SCRIPTED LESSON MODE
// ------------------------
if (activeLesson) {
  try {
    // First time: start playback (speaks first SRT cue)
    if (!scriptedPlayingRef.current) {
      scriptedPlayingRef.current = true;
      const firstText = await startCTPlayback(activeLesson);

      setMessages([...baseNext, { role: "assistant", content: firstText }]);
      waitingForSpeakEndRef.current = true;
      return; // IMPORTANT: do NOT call AI
    }

    // Already playing: user input acts like "continue"
    const nextText = await nextCTCue(activeLesson);

    if (nextText) {
      setMessages([...baseNext, { role: "assistant", content: nextText }]);
      waitingForSpeakEndRef.current = true;
      return;
    }

    // End of cues
    scriptedPlayingRef.current = false;
    setMessages([...baseNext, { role: "assistant", content: "Klaar! Wil je doorgaan naar het volgende onderdeel?" }]);
    return;
  } finally {
    setLoading(false);
  }
}

    forceStickToBottomRef.current = true;
    userScrolledAwayRef.current = false;
    requestAnimationFrame(() => {
      lastUserBubbleRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
      rightBottomRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
    });

    try {
      if (aiProvider === "ollama") {
        const controlPrompt = buildControlPromptFromProfile(activeProfile, gameState, text);
        const assembled: Msg[] = [
          baseNext[0],
          { role: "system", content: controlPrompt },
          ...baseNext.slice(1),
        ];

        const res = await fetch(ollamaUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: DEFAULT_MODEL,
            messages: assembled,
            stream: false,
            options: { num_predict: 320 },
          }),
        });

        const raw = await res.text();
        if (!res.ok) throw new Error(raw);
        const data = JSON.parse(raw);
        const reply = String(data?.message?.content ?? "Hmm ik kreeg geen antwoord.");

        const withAssistant: Msg[] = [...baseNext, { role: "assistant", content: reply }];
        setMessages(withAssistant);

        // Classify assistant response
        const assistantEvent = activeProfile.classifyAssistantResponse(reply, null, gameState);
        setGameState(prev => advanceGameState(activeProfile, prev, assistantEvent));
      } else {
        // ---- OpenAI path ----
// Build a model-facing message list where the last user message is modelText
const modelNext: Msg[] = [...messages, { role: "user", content: modelText }];

const payload = await callOpenAIChat(modelNext, modelText, nextState);
let { reply, script, board } = payload;

// ✅ If reply accidentally contains a JSON object, unwrap it.
// This fixes the “JSON printed in chat” problem even when the proxy double-wraps.
const r = (reply || "").trim();
if (r.startsWith("{") && r.includes('"reply"')) {
  try {
    const inner = JSON.parse(r);
    reply = String(inner.reply || "").trim();
    script = inner.script ?? script ?? null;
    board = inner.board ?? inner?.script?.beats?.[0]?.board ?? board ?? null;
  } catch {
    // keep reply as-is
  }
}        // Write reply to chat
      

// ---- Board handling FIRST ----
// RULE:
// - avatar-driven profiles may supply board JSON (boardOverride)
// - inferred profiles NEVER supply board JSON (board must come from digiboard.php)
if (activeProfile.boardSource === "avatar-driven") {
  let boardFromResponse: any = board ?? null;

  if (!boardFromResponse && script && typeof script === "object") {
    const beats = Array.isArray((script as any).beats) ? (script as any).beats : [];
    boardFromResponse = (script as any).board ?? beats[0]?.board ?? null;
  }

  if (boardFromResponse && Array.isArray(boardFromResponse.blocks)) {
    const parsed = safeParseBoard(boardFromResponse);
    if (parsed) {
      const candidate = {
        version: 2,
        scene: boardFromResponse.scene || { title: "Les" },
        meta: { mode: "explanation" as const },
        blocks: parsed.blocks,
        layout: boardFromResponse.layout,
        phase: boardFromResponse.phase,
      };

      setBoardOverride(gatekeepBoard(activeProfile, nextState.phase, candidate));
    } else {
      setBoardOverride(null);
    }
  } else {
    setBoardOverride(null);
  }
} else {
  // inferred (DT) + other non-avatar-driven profiles:
  // always let DigiBoardPanel fetch from digiboard.php
  setBoardOverride(null);
}

// NOW write reply to chat (after board is ready)
const withAssistant: Msg[] = [
  ...baseNext,
  { role: "assistant", content: reply },
];
setMessages(withAssistant);


        

        // Classify assistant response and advance state
       const assistantEvent = activeProfile.classifyAssistantResponse(reply, script, nextState);
setGameState(() => advanceGameState(activeProfile, nextState, assistantEvent));
      }
   } catch (e: any) {
  console.error("[send] failed", e);
  setMessages([
    ...baseNext,
    {
      role: "assistant",
      content:
        aiProvider === "ollama"
          ? "Oeps — ik kan Ollama niet bereiken. Staat Ollama aan?"
          : "Oeps — ik kan de online AI niet bereiken: " + String(e?.message || e),
    },
  ]);
} finally {
  setLoading(false);
}
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ---- Determine board forcedMode from profile ----
const boardForcedMode = React.useMemo(() => {
  // ✅ absolute rule: until user speaks, Digiboard is intro (if ready gate enabled)
  if (!hasUserSpoken && activeProfile.readyGateEnabled) return "intro" as const;

const layout = activeProfile.layoutByPhase[boardPhase];
  const isDT = activeProfile.id === "design-thinking";

  const layoutIsIntro =
    layout === "intro" ||
    (!isDT && typeof layout === "string" && layout.toLowerCase().includes("intro"));

  const phaseIsIntro =
  activeProfile.readyGateEnabled &&
  (boardPhase === "INTRO" ||
    boardPhase === "READY_WAIT" ||
    (!isDT && boardPhase === activeProfile.initialPhase));

  if (layoutIsIntro || phaseIsIntro) return "intro" as const;
  if (layout === "question") return "question" as const;
  return "explanation" as const;
}, [activeProfile, enginePhase, hasUserSpoken]);

  return (
    <div className="chatls-page">
      {/* LEFT: DIGIBOARD */}
      <div className="chatls-left">
        <div className="chatls-screen">
          <div className="chatls-thread" role="region" aria-label="Digiboard">
            <DigiBoardPanel
              systemPrompt={systemPrompt}
              messages={messages as unknown as DigiMsg[]}
              digiboardUrl={digiboardUrl}
              boardOverride={boardOverride}
              forcedMode={boardForcedMode}
              profileId={activeProfile.id}
  gamePhase={boardPhase}
                activeLesson={activeLesson}   // ✅ ADD THIS
            />
          </div>

          {/* INPUT */}
          <div className="chatls-inputRow">
            <textarea
              className="chatls-input"
              placeholder="Typ hier (Enter = verstuur, Shift+Enter = nieuwe regel)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={3}
            />
            <div className="chatls-actions">
              <button className="chatls-btn chatls-btnPrimary" onClick={send} type="button" disabled={loading}>
                {loading ? "\u2026" : "Stuur"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: CHAT + AVATAR */}
      <div className="chatls-right">
        <div className="chatls-bubbleList" ref={transcriptRef} onScroll={onTranscriptScroll} role="log" aria-live="polite">
          {visibleMessages.map((m, midx) => {
            const isUser = m.role === "user";
            const isActiveAssistant = m.role === "assistant" && midx === lastAssistantVisibleIndex;

            if (isUser) {
              return (
                <div
                  key={midx}
                  ref={midx === lastUserVisibleIndex ? lastUserBubbleRef : undefined}
                  className="chatls-bubbleMsg chatls-bubbleMsg-user"
                >
                  <div className="chatls-bubbleText">{m.content}</div>
                </div>
              );
            }

            const assistantText = m.content || "";
            const { pages } = isActiveAssistant ? { pages: currentPages } : chunkTextWithStarts(assistantText, 90);

            return (
              <div key={midx} className="chatls-bubbleMsg chatls-bubbleMsg-ai">
                {pages.map((p, i) => {
                  const isActiveChunk = isActiveAssistant && isSpeaking && i === pageIndex;
                  return (
                    <div
                      key={i}
                      ref={isActiveChunk ? activeChunkRef : undefined}
                      className={`chatls-bubbleText ${isActiveChunk ? "is-active" : ""}`}
                    >
                      {p}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div ref={rightBottomRef} />
        </div>

        <div className="chatls-speakingRow" aria-hidden="true">
          <div className="chatls-bubbleDots" style={{ opacity: isSpeaking ? 1 : 0.25 }}>
            <span className="chatls-dot" />
            <span className="chatls-dot" />
            <span className="chatls-dot" />
          </div>
        </div>

        <div className="chatls-avatar">
          <BellaRiveBig />
          <div className="chatls-avatarControls">
            <TalkingAvatar
              user={{ name: "Bella", avatar: "bella" }}
              textToSpeak={lastAssistant}
              autoSpeak
              avatar="bella"
              voiceProvider={voiceProvider}
              onSpeakChange={setIsSpeaking}
              onBoundary={setCharIndex}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
